import React, { ReactNode } from 'react';
import reportWebVitals from './reportWebVitals';
import { initAssignUnitSelect } from './admin/order/assign-unit-select/init';
import { initInventoryDashboard } from './admin/inventory-dashboard/init';
import { initProductForm } from './customer/product-form/init';
import { initAssignedUnits } from './admin/order/assigned-units/init';
import { initPricingTab } from './admin/pricing-tab/init';
import { initWorkOrderList } from './admin/service-and-repair/work-orders/init';
import { initProductPricingTable } from './customer/product-pricing-table/init';
import { initProductsReport } from './admin/report/products/init';
import { initActionWidget } from './admin/action-widget/init';
import './index.scss';
import './index.css';
import ReactDOM from 'react-dom/client';
import { App } from './App';
import { initProductUnitsTab } from './admin/product/product-unit-tab/init';
import { initLocationSelect } from './admin/location/select/init';
import { initProductList } from './admin/product/list/init';
import { initDeadDaysConfig } from './admin/pricing-tab/dead-days-config/init';
import { initCustomerList } from './admin/customer/list/init';
import { initAddProduct } from './admin/order/add-product/init';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { initQboAccount, initQboItemIncomeAccount, initQboProductIncomeAccount } from './admin/qbo/init';
import { initTransactionsReport } from './admin/report/transactions/init';
import { initUserSetup } from './admin/customer/page/init';
import { initOrdersView } from './admin/order/init';
import { initCategoryList } from './admin/category/list/init';
import { initRentersReport } from './admin/report/users/init';
import { initAccountRequestList } from './admin/customer/account-requests/init';
import { initLocationList } from './admin/location/list/init';
import { initSocialLoginComponent } from './customer/social-login/init';
import { initCardPaymentComponent } from './customer/payment/stripe/setup-component/init';
import { initPayLaterPlanMessaging } from './customer/payment/pay-later-plan-messaging/init';
import { initOrderDetails } from './admin/order/invoices/init';
import { initSelectPaymentComponent } from './customer/payment/select-payment/init';
import { initAdminCardPaymentComponent } from './admin/payment/stripe/setup-component/init';
import { initCustomerOrders } from './customer/orders/init';
import { initCustomerTerminalComponent } from './customer/payment/stripe/terminal-component/init';
import { initProductUnitsList } from './admin/product-units/init';
import { initApproveQuote } from './admin/order/approve-quote/init';
import { initOrderAuthentication } from './customer/payment/payment-authorization/init';
import { initOrderStatusTransitionButtons } from './admin/order/status-transitions/init';
import { initEmailHistory } from './admin/order/email-history/init';
import { initOrderStatusTransitionPaymentRequirementConfig } from './admin/settings/order-status-transition-payment-requirement-config/init';
import { initOrderView } from './admin/order/view/init';
import { initProductImages } from './admin/product/product-images/init';
import { initItemGeneralLedgerAccount, initProductGeneralLedgerAccount } from './admin/accounting/init';
import { initStripePayoutReconciliationReport } from './admin/report/payout/init';
import { initProductImage } from './components/image/product/init';
import { initHourTrackingConfigForm } from './admin/product/components/hour-tracking-config/init';
import { initFuelChargingConfigForm } from './admin/product/components/fuel-charging-config/init';
import { initInsuranceForm } from './customer/insurance/init';
import { initViewProductV2 } from './customer/products/product-details/init';
import { initProductPlaceholder } from './admin/settings/product-placeholder/init';
import { initUnpaidBatch } from './admin/settings/unpaid-batch/init';
import { initProductDescriptionForm } from './admin/product/components/description/init';
import { initTextEditor } from './components/text-editor/init';
import { initCheckoutView } from './customer/checkout/init';
import { initMinimumDurationPriceConfig } from './admin/product/components/minimum-duration-price-config/init';
import { initFeaturedProductsV2 } from './customer/products/featured-products/init';
import { initOrderRequestForm } from './customer/order-request/init';
import { initCategoryAliasesField, initProductAliasesField } from './admin/product/components/aliases-field/init';
import { initProductSearchResults } from './customer/products/search-results/init';
import { initQuoteView } from './customer/checkout/quote-view/init';
import { initInvoiceLogoUpload } from './admin/settings/invoice-logo/init';
import { initDeliveryChargesConfig } from './admin/settings/delivery-charges-config/init';
import { initProductDeliveryConfig } from './admin/product/components/delivery-config/init';
import { initCycleBillingDurationConfig } from './admin/settings/init';
import { initFeeDisclaimerConfig } from './admin/settings/fee-disclaimer-config/init';
import { initOnAccountNumberFormatConfig } from './admin/settings/on-account-number-format-config/init';
import { initProductTaxCategoryInput, initItemTaxCategoryInput } from './admin/tax-setup/init';
import { initProductDepreciationConfig } from './admin/product/product-depreciation-config/init';
import { GTMEvent } from './googleTagManager';
import { initTaxConfig } from './admin/settings/tax-config/init';
import { initAssignInspectionWorkflow } from './admin/job-tasks/assign-inspection-workflow/init';
// @hipaa-exclude
import LogRocket from 'logrocket';
import { initOrderStatusRequirementConfig } from './admin/settings/order-status-requirement-config/init';
import { initCustomProductPricingConfig } from './admin/settings/custom-product-pricing-config/init';
import { initOrderNumberFormatConfig } from './admin/settings/order-number-format-config/init';
import { initLicenseDetails, initLicenseScan } from './customer/license/init';
import { Userpilot } from 'userpilot';

declare global {
  interface Window {
    dataLayer?: {
      push: (e: GTMEvent) => void;
    };
  }
}

(function removeDoubleSlashWhenLogin() {
  if (window.location.pathname.includes('//')) {
    const cleanUrl = window.location.href.replace(/([^:])(\/\/+)/g, '$1/');
    window.location.href = cleanUrl;
  }
})();

const rootElement = document.getElementById('root');
if (rootElement) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}

initComponents();

document.body.addEventListener('init-components', () => {
  initComponents();
});

function initComponents() {
  //Admin
  initAssignUnitSelect();
  initAssignedUnits();
  initInventoryDashboard();
  initPricingTab();
  initWorkOrderList();
  initProductsReport();
  initActionWidget();
  initLocationSelect();
  initProductList();
  initDeadDaysConfig();
  initCustomerList();
  initAddProduct();
  initQboAccount();
  initQboItemIncomeAccount();
  initQboProductIncomeAccount();
  initItemGeneralLedgerAccount();
  initProductGeneralLedgerAccount();
  initTransactionsReport();
  initUserSetup();
  initCategoryList();
  initRentersReport();
  initAccountRequestList();
  initLocationList();
  initAdminCardPaymentComponent();
  initProductUnitsList();
  initEmailHistory();
  initOrderStatusTransitionPaymentRequirementConfig();
  initOrderStatusRequirementConfig();
  initProductUnitsTab();
  initProductImages();
  initHourTrackingConfigForm();
  initFuelChargingConfigForm();
  initStripePayoutReconciliationReport();
  initProductPlaceholder();
  initUnpaidBatch();
  initProductDescriptionForm();
  initTextEditor();
  initMinimumDurationPriceConfig();
  initCategoryAliasesField();
  initProductAliasesField();
  initInvoiceLogoUpload();
  initDeliveryChargesConfig();
  initProductDeliveryConfig();
  initCycleBillingDurationConfig();
  initOrderNumberFormatConfig();
  initFeeDisclaimerConfig();
  initOnAccountNumberFormatConfig();
  initTaxConfig();
  initAssignInspectionWorkflow();
  initCustomProductPricingConfig();

  // Orders
  initOrdersView();
  initOrderDetails();
  initApproveQuote();
  initOrderStatusTransitionButtons();
  initOrderView();

  //Customer
  initProductForm();
  initOrderRequestForm();
  initProductPricingTable();
  initSocialLoginComponent();
  initCardPaymentComponent();
  initPayLaterPlanMessaging();
  initSelectPaymentComponent();
  initAdminCardPaymentComponent();
  initCustomerOrders();
  initCustomerTerminalComponent();
  initOrderAuthentication();
  initProductImage();
  initInsuranceForm();
  initViewProductV2();
  initCheckoutView();
  initFeaturedProductsV2();
  initProductSearchResults();
  initQuoteView();
  initItemTaxCategoryInput();
  initLicenseScan();
  initLicenseDetails();

  //Product
  initProductTaxCategoryInput();
  initProductDepreciationConfig();
}

export function v2Component(component: ReactNode): ReactNode {
  return (
    <React.StrictMode>
      <BrowserRouter>
        <Routes>
          <Route path="*" element={<div className="quipli-v2">{component}</div>} />
        </Routes>
      </BrowserRouter>
    </React.StrictMode>
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
// @hipaa-exclude-start
if (window.location.host === 'dev.quipli.com') {
  LogRocket.init('kyog6d/quipli-dev');
} else if (window.location.host === 'qa.quipli.com') {
  LogRocket.init('kyog6d/quipli-qa');
} else if (window.location.host === 'integration.quipli.com') {
  LogRocket.init('kyog6d/quipli-integration');
} else if (!window.location.host.startsWith('localhost')) {
  LogRocket.init('kyog6d/quipli-prod');
}
if (window.location.host == 'beta.quipli.com' && window.location.pathname.includes('devops')) {
  Userpilot.initialize('STG-NX-722c7e56');
} //initialize prod userpilot
// @hipaa-exclude-end
